/**
 * Description — renders HTML description with expand/collapse.
 * Hidden if description is empty (Lazada only).
 *
 * TikTok: Tap opens a side sheet editor for editing.
 * Shows dirty indicator when description has been modified.
 * Always shows the card (even empty) so user can add description.
 */

import { useState, useRef, useEffect } from "react";
import { Pencil } from "lucide-react";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig } from "./shared";
import {
  useTikTokEditStore,
  selectFieldValue,
  selectIsFieldDirty,
} from "../stores/useTikTokEditStore";
import { DescriptionBottomSheet } from "./DescriptionBottomSheet";

export function MarketplaceDescriptionWidget({ section, formData }: ExperienceWidgetProps) {
  const channel = section.description || "";
  const config = getChannelConfig(channel);
  const isTikTok = channel === "TikTok Shop";

  const rawDescription = formData?.[config.description] as string | undefined;

  // For TikTok, use the store value (which may be dirty)
  const storeDescription = useTikTokEditStore(selectFieldValue("description"));
  const isDescDirty = useTikTokEditStore(selectIsFieldDirty("description"));

  const description = isTikTok ? (storeDescription ?? rawDescription) : rawDescription;

  const [expanded, setExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setNeedsTruncation(el.scrollHeight > el.clientHeight);
    }
  }, [description]);

  // For Lazada: hide if empty. For TikTok: always show so user can add.
  if (!isTikTok && (!description || description.trim() === "")) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2 flex items-center gap-2">
        Description
        {isTikTok && isDescDirty && (
          <span className="inline-block w-2 h-2 rounded-full bg-primary" title="Modified" />
        )}
      </h3>
      <div
        className={`rounded-2xl overflow-hidden bg-card px-4 py-5 ${isTikTok ? "cursor-pointer hover:bg-accent/50 transition-colors" : ""}`}
        onClick={isTikTok ? () => setSheetOpen(true) : undefined}
        role={isTikTok ? "button" : undefined}
        tabIndex={isTikTok ? 0 : undefined}
        onKeyDown={
          isTikTok
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") setSheetOpen(true);
              }
            : undefined
        }
      >
        {/* Edit hint for TikTok */}
        {isTikTok && (
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Tap to edit</span>
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}

        {description && description.trim() !== "" ? (
          <div className="relative">
            <div
              ref={contentRef}
              className={`prose prose-sm max-w-none ${!expanded && needsTruncation ? "max-h-48 overflow-hidden" : ""}`}
              dangerouslySetInnerHTML={{ __html: description }}
            />
            {!expanded && needsTruncation && (
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card to-transparent" />
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No description — tap to add</p>
        )}

        {!isTikTok && needsTruncation && (
          <button
            type="button"
            className="mt-2 text-sm text-primary hover:underline"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {/* Description editor sheet (TikTok only) */}
      {isTikTok && (
        <DescriptionBottomSheet open={sheetOpen} onOpenChange={setSheetOpen} />
      )}
    </div>
  );
}
